import orderBy from 'lodash/orderBy'
import { observer } from 'mobx-react'
import { useMemo } from 'react'

import { CheckboxBasedFilter, OptionsProps } from '@avantstay/backoffice-core'

import { casesFilterOptionsStore } from '@new/domains/operations/screens/CasesV2/stores/CasesFilterOptionsStore'
import { casesFiltersStore } from '@new/domains/operations/screens/CasesV2/stores/CasesFiltersStore'
import { casesStore } from '@new/domains/operations/screens/CasesV2/stores/CasesStore'
import { CaseFilterIds } from '@new/domains/operations/screens/CasesV2/utils/config'
import {
  buildTaxonomyServiceTypeOptions,
  oldTaxonomyGroup,
  TaxonomyFilterLevel,
} from '@new/domains/operations/screens/CasesV2/utils/taxonomyFilterOptions'
import { parseLabel } from '@new/domains/operations/utils/parseLabel'

import * as S from './commonFilterStyles.style'
import { useTaxonomyColumnFilter } from './useTaxonomyColumnFilter'

const _TypeFilter = () => {
  const { caseInputs, isLoadingCaseInputs } = casesStore
  const { taxonomyTasks, isLoadingTaxonomyTasks } = casesFilterOptionsStore
  const {
    recordTypes,
    taxonomyRecordTypes,
    types,
    taxonomyTypes,
    shouldShowLegacyFilterOptions,
    shouldShowTaxonomyFilterOptions,
  } = casesFiltersStore

  const taxonomyOptions = useMemo(
    () =>
      shouldShowTaxonomyFilterOptions
        ? orderBy(
            buildTaxonomyServiceTypeOptions(taxonomyTasks, { taskNodeIds: taxonomyRecordTypes }),
            ['group', 'label'],
          )
        : [],
    [taxonomyTasks, taxonomyRecordTypes, shouldShowTaxonomyFilterOptions],
  )

  const options = useMemo(() => {
    if (!caseInputs || !shouldShowLegacyFilterOptions) return taxonomyOptions
    const legacyOptions = Object.entries(caseInputs).reduce<OptionsProps<string>[]>(
      (acc, [recordType, caseInput]) => {
        if (recordTypes && !recordTypes.includes(recordType)) return acc
        const groupName = oldTaxonomyGroup(parseLabel(recordType))

        const listByRecordType =
          caseInput.caseTypes?.map(type => ({
            label: parseLabel(type.caseType.label),
            value: type.caseType.apiName,
            group: groupName,
            hasFullWidth: true,
          })) ?? []
        return [...acc, ...listByRecordType]
      },
      [],
    )
    return [...taxonomyOptions, ...orderBy(legacyOptions, ['group', 'label'])]
  }, [caseInputs, recordTypes, taxonomyOptions, shouldShowLegacyFilterOptions])

  const { initialValue, handleDataFilter } = useTaxonomyColumnFilter({
    legacyFilterId: CaseFilterIds.Type,
    level: TaxonomyFilterLevel.ServiceTypes,
    legacyValues: types,
    taxonomyValues: taxonomyTypes,
    taxonomyOptions,
  })

  return (
    <S.CheckBoxWrapper optionsLength={options.length}>
      <CheckboxBasedFilter
        isLoadingOptions={isLoadingCaseInputs || isLoadingTaxonomyTasks}
        shouldGroupBy
        title="Types"
        options={options}
        initialValue={initialValue}
        shouldUpdateOnInitialValueChange
        onChange={handleDataFilter}
      />
    </S.CheckBoxWrapper>
  )
}

export const TypeFilter = observer(_TypeFilter)
